import type { Caught, Paper } from '../net/protocol';
import { shortLap } from '../net/lapTime';
import { shortCircuit } from '../track/names';

export { shortCircuit };

/** What the paper needs besides yesterday's race: the two days' circuits and cars, and today's date. */
export interface PaperDays {
  /** Today, as the dateline prints it ("thursday 8 october 2026"). */
  dateLabel: string;
  /** The paper's number: one a day since the game opened. */
  issue: number;
  yesterday: { circuit: string; region: string; car: string };
  today: { circuit: string; region: string; car: string };
}

/** Everything the paper prints, worked out from the race (ui/paper.ts lays it out). */
export interface PaperWords {
  top: [string, string, string];
  dateline: [string, string];
  head: string;
  deck: string;
  caption: string;
  podium: { place: number; name: string; time: string }[];
  drivers: string;
  story: string;
  /** Your column, or the note for a day missed. */
  yours: { kick: string; big: string; of: string; line: string; stamps: string[]; rival: { name: string; text: string } | null; gold: string | null } | null;
  /** The clipping's headline (the shared picture is about you). */
  clipHead: string;
  clipDeck: string;
  away: { head: string; text: string } | null;
  today: [string, string];
  /** Yesterday's winner reading their own paper. */
  champion: boolean;
  /** The corner of shame: yesterday's made-up laps, one line each. */
  caught: { name: string; text: string }[];
}

/** The day the game opened: issue number 1. */
export const FIRST_ISSUE = '2026-10-02';


const WORDS = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** A winning margin as a sports page writes it: "two hundredths", "a tenth", "0.48 s". */
export function marginWords(ms: number): string {
  if (ms < 10) return 'a whisker';
  if (ms < 100) {
    const n = Math.round(ms / 10);
    return n === 10 ? 'a tenth' : `${WORDS[n]} hundredth${n === 1 ? '' : 's'}`;
  }
  if (ms < 150) return 'a tenth';
  return `${(ms / 1000).toFixed(2)} s`;
}

const ordinal = (n: number): string => `${n}${n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th'}`;

/** The days' numbers apart. */
function daysBetween(a: string, b: string): number {
  const t = (d: string): number => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
  return Math.round((t(b) - t(a)) / 86400000);
}

export function issueOf(today: string): number {
  return Math.max(1, daysBetween(FIRST_ISSUE, today) + 1);
}

/** The front page's furniture, the same every morning: the masthead's line, the dateline, today's race at the foot. */
function masthead(d: PaperDays): Pick<PaperWords, 'top' | 'dateline' | 'today'> {
  return {
    top: [`anno I · n. ${d.issue}`, 'quotidiano sportivo', '50 lire'],
    dateline: [d.dateLabel, `yesterday at ${d.yesterday.circuit} · ${d.yesterday.region}`.toLowerCase()],
    today: [`today · ${d.today.circuit} · ${d.today.car} · ${d.today.region}`.toLowerCase(), 'closes at midnight'],
  };
}

const captionOf = (names: string[]): string =>
  names.length === 3 ? `${names[0]} lifts the cup, ${names[1]} and ${names[2]} alongside` : names.length === 2 ? `${names[0]} lifts the cup, ${names[1]} alongside` : `${names[0] ?? ''} lifts the cup`;

/** The podium's table: the winner's time, then each one's gap to it. */
const podiumLines = (podium: readonly { place: number; name: string; lapMs: number }[]): PaperWords['podium'] =>
  podium.map((x, i) => ({ place: x.place, name: x.name, time: i === 0 ? shortLap(x.lapMs) : `+${((x.lapMs - podium[0].lapMs) / 1000).toFixed(3)}` }));

const listed = (xs: readonly string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** A made-up lap in the corner of shame, as the paper reports it. */
export function caughtLine(c: Caught): { name: string; text: string } {
  const aka = c.aka.length ? `, also signing as ${listed(c.aka)},` : '';
  return {
    name: c.name ?? 'A driver with a borrowed name',
    text: `${aka} handed in a ${shortLap(c.claimedMs)}. The stewards drove it again: ${shortLap(c.droveMs)}.${c.tries > 1 ? ` ${c.tries} tries.` : ''}`,
  };
}

/** The paper's words, from yesterday's race and the two days. */
export function paperWords(p: Paper, d: PaperDays): PaperWords {
  const [first, second] = p.podium;
  const short = shortCircuit(d.yesterday.circuit);
  const margin = first && second ? second.lapMs - first.lapMs : null;
  const you = p.you;
  const champion = you?.rank === 1;
  const names = p.podium.map((x) => x.name);
  const story = !first
    ? ''
    : margin === null
      ? `A lone run at ${short}: ${first.name} had the circuit to themselves.`
      : margin < 100
        ? `Decided by hundredths: ${first.name} held off ${second.name} to midnight.`
        : `${first.name} finished ${marginWords(margin)} clear of ${second.name}.`;
  let yours: PaperWords['yours'] = null;
  if (you) {
    const top = Math.max(1, Math.ceil((you.rank / Math.max(1, p.total)) * 100));
    const parts = [shortLap(you.lapMs)];
    if (champion) {
      if (margin !== null) parts.push(`${marginWords(margin)} ahead of ${second.name}`);
    } else {
      // "Top 12%" is worth saying; "top 100%" isn't.
      if (you.rank > 3 && top <= 50) parts.push(`top ${top}%`);
      if (you.nextGapMs !== null) parts.push(`${(you.nextGapMs / 1000).toFixed(2)} from P${you.rank - 1}`);
    }
    const stamps = [
      champion ? 'winner' : you.rank <= 3 ? `${ordinal(you.rank)} place` : null,
      p.streak >= 2 ? `${p.streak} days in a row` : null,
      you.bestFinish ? 'best finish yet' : null,
      you.prevRank !== null && you.prevRank > you.rank ? `up ${you.prevRank - you.rank} place${you.prevRank - you.rank === 1 ? '' : 's'}` : null,
      you.rank > 3 && you.rank <= 10 ? 'top 10' : null,
    ].filter((s): s is string => s !== null);
    yours = {
      kick: `your race · ${you.name}`,
      big: champion ? '1st' : `P${you.rank}`,
      of: `of ${p.total}`,
      line: parts.join(' · '),
      stamps: stamps.slice(0, 3),
      rival: you.rival ? { name: you.rival.name, text: ` beat you by ${(you.rival.byMs / 1000).toFixed(2)} · today’s your rematch` } : null,
      gold: champion ? 'your car wears gold today' : null,
    };
  }
  return {
    ...masthead(d),
    head: first ? `${first.name} wins at ${short}` : `Yesterday at ${short}`,
    deck: first ? [d.yesterday.car, shortLap(first.lapMs), margin !== null ? `${marginWords(margin)} ahead of ${second.name}` : null].filter(Boolean).join(' · ') : '',
    caption: captionOf(names),
    podium: podiumLines(p.podium),
    drivers: `${p.total} driver${p.total === 1 ? '' : 's'} raced yesterday.`,
    story,
    yours,
    caught: (p.caught ?? []).map(caughtLine),
    away: you ? null : { head: 'you weren’t racing yesterday', text: `Your seat at ${short} stayed empty. Today’s race is waiting below.` },
    clipHead: !you ? (first ? `${first.name} wins at ${short}` : `Yesterday at ${short}`) : champion ? `${you.name} wins at ${short}` : you.rank <= 3 ? `${you.name} takes ${ordinal(you.rank)} at ${short}` : `${you.name} takes P${you.rank} of ${p.total}`,
    clipDeck: !you ? '' : [`${shortLap(you.lapMs)} at ${short}`, champion && margin !== null ? `${marginWords(margin)} ahead of ${second.name}` : null, !champion && you.bestFinish ? 'a best finish' : null].filter(Boolean).join(' · '),
    champion,
  };
}

/** A Discord server's race yesterday, for its own front page in its channel (worker/discordPaper.ts). */
export interface ServerRace {
  server: string;
  /** Its podium on yesterday's main board (names the filter lets through), fastest first. */
  podium: { place: number; name: string; lapMs: number }[];
  /** How many of its players drove it. */
  drivers: number;
  /** The whole race: its winner (`ours`: the server's own winner, too) and how many drove it. */
  overall: { name: string; lapMs: number; total: number; ours: boolean };
}

/** A server's front page: its winner in the headline, its podium in the photo and the table, a line on the whole race. */
export function serverPaperWords(r: ServerRace, d: PaperDays): PaperWords {
  const [first, second] = r.podium;
  const short = shortCircuit(d.yesterday.circuit);
  const margin = first && second ? second.lapMs - first.lapMs : null;
  const o = r.overall;
  const drivers = (n: number): string => `${n} driver${n === 1 ? '' : 's'}`;
  return {
    ...masthead(d),
    head: first ? `${first.name} wins for ${r.server}` : `${r.server} at ${short}`,
    deck: [d.yesterday.circuit, d.yesterday.car, first ? shortLap(first.lapMs) : null, margin !== null ? `${marginWords(margin)} ahead of ${second.name}` : null]
      .filter(Boolean)
      .join(' · '),
    caption: captionOf(r.podium.map((x) => x.name)),
    podium: podiumLines(r.podium),
    drivers: `${drivers(r.drivers)} raced for ${r.server} yesterday.`,
    story:
      o.ours && first
        ? o.total > r.drivers
          ? `${first.name} won the whole race too: the fastest of all ${drivers(o.total)} on the board.`
          : `${first.name} was the fastest on the whole board, too.`
        : `Overall, ${o.name} won at ${short} in ${shortLap(o.lapMs)}, with ${drivers(o.total)} on the board.`,
    yours: null,
    clipHead: '',
    clipDeck: '',
    away: null,
    champion: false,
    caught: [],
  };
}

/** The text that goes with the paper's clipping when it's shared. */
export function paperShareText(p: Paper, d: PaperDays, url: string): string {
  const short = shortCircuit(d.yesterday.circuit);
  const you = p.you;
  const line = !you
    ? `${p.podium[0]?.name ?? 'Someone'} won at ${short}`
    : you.rank === 1
      ? `I won at ${short} · ${shortLap(you.lapMs)}`
      : `P${you.rank} of ${p.total} at ${short} · ${shortLap(you.lapMs)}`;
  return ['📰 Grand Premio · yesterday’s paper', line, p.streak >= 2 ? `${p.streak} days in a row` : null, `${you?.rank === 1 ? 'Beat the champion' : 'Race me'} today: ${url}`]
    .filter((s): s is string => s !== null)
    .join('\n');
}
